import { type Spec, createSpecStreamCompiler, defineCatalog } from "@json-render/core";
import { schema } from "@json-render/react/schema";
import { z } from "zod";

export { createSpecStreamCompiler, type Spec };
const id = z
  .string()
  .regex(/^[a-zA-Z][a-zA-Z0-9_-]{0,47}$/)
  .refine((value) => !["constructor", "prototype", "__proto__"].includes(value));
const n = z.number().finite().min(-1000).max(1000);
// The worker strips nulls before publishing, so optional fields may be absent as well as null.
const label = z.string().min(1).max(40).nullish();
const mark = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("function"),
      expression: z.string().min(1).max(80),
      label,
      points: z.array(z.tuple([n, z.number().finite()]).nullable()).max(97),
    })
    .strict(),
  z.object({ type: z.literal("point"), x: n, y: n, label }).strict(),
  z
    .object({ type: z.literal("segment"), x1: n, y1: n, x2: n, y2: n, arrow: z.boolean(), label })
    .strict(),
  z
    .object({
      type: z.literal("rect"),
      x: n,
      y: n,
      width: n,
      height: n,
      filled: z.boolean(),
      label,
    })
    .strict(),
  z
    .object({ type: z.literal("circle"), x: n, y: n, radius: n, filled: z.boolean(), label })
    .strict(),
  z.object({ type: z.literal("text"), x: n, y: n, text: z.string().min(1).max(60) }).strict(),
  z
    .object({
      type: z.literal("fraction_bar"),
      x: n,
      y: n,
      width: n,
      height: n,
      parts: z.number().int().min(1).max(24),
      shaded: z.number().int().min(0).max(24),
      label,
    })
    .strict(),
]);
export const figureProps = z
  .object({
    title: z.string().min(1).max(60).nullish(),
    axes: z.enum(["none", "x", "xy"]),
    x_min: n,
    x_max: n,
    y_min: n,
    y_max: n,
    marks: z.array(mark).min(1).max(16),
  })
  .strict()
  .refine((value) => value.x_min < value.x_max && value.y_min < value.y_max);
export type FigureProps = z.infer<typeof figureProps>;
export type FigureMark = z.infer<typeof mark>;
export const componentProps = {
  Stack: z.object({}).strict(),
  Panel: z.object({}).strict(),
  Heading: z.object({ text: z.string().min(1).max(120) }).strict(),
  Text: z.object({ text: z.string().min(1).max(900) }).strict(),
  Table: z
    .object({
      headers: z.array(z.string().min(1).max(60)).min(1).max(3),
      rows: z
        .array(z.array(z.string().max(100)).min(1).max(3))
        .min(1)
        .max(6),
    })
    .strict()
    .refine((value) => value.rows.every((row) => row.length === value.headers.length)),
  // 60/480 requested, accepted up to 1.5x (lib/whiteboard/streaming/textLimits.ts); mirrors the worker contract.
  Reveal: z.object({ label: z.string().min(1).max(90), text: z.string().min(1).max(720) }).strict(),
  Activity: z.object({ activityId: id }).strict(),
  Exercise: z.object({ exerciseId: id }).strict(),
  // Drawing data only (lib/maths_figure.py); the host draws it. Function points are worker-sampled.
  Figure: figureProps,
};
export const catalog = defineCatalog(schema, {
  components: {
    Stack: {
      props: componentProps.Stack,
      slots: ["default"],
      description: "Vertical teaching blocks",
    },
    Panel: {
      props: componentProps.Panel,
      slots: ["default"],
      description: "Grouped teaching blocks",
    },
    Heading: { props: componentProps.Heading, description: "Short board heading" },
    Text: { props: componentProps.Text, description: "Coherent explanation or reading paragraph" },
    Table: { props: componentProps.Table, description: "Small conjugation or comparison table" },
    Reveal: { props: componentProps.Reveal, description: "Optional extra help" },
    Activity: { props: componentProps.Activity, description: "Host-owned practice controls" },
    Exercise: {
      props: componentProps.Exercise,
      description: "Native first-party exercise controls",
    },
    Figure: { props: componentProps.Figure, description: "Validated drawing data" },
  },
  actions: {},
});
export function parseElement(value: unknown) {
  const element = z
    .object({
      type: z.enum([
        "Stack",
        "Panel",
        "Heading",
        "Text",
        "Table",
        "Reveal",
        "Activity",
        "Exercise",
        "Figure",
      ]),
      props: z.record(z.string(), z.unknown()),
      children: z.array(id).max(12),
    })
    .strict()
    .parse(value);
  const props = componentProps[element.type].parse(element.props);
  if (!["Stack", "Panel"].includes(element.type) && element.children.length)
    throw new Error("Leaf children");
  return { ...element, props } as Spec["elements"][string];
}
